import { useEffect, useState } from "react";
import api from "../lib/api";
import { useResponsive } from "../lib/useResponsive";

export interface Partner {
  /** The partner's lower-cased email, which is its key. */
  id: string;
  name: string;
  email: string;
  nominee: string;
}

export interface FormState {
  date_of_deposit: string;
  action: "Deposit" | "Initial";
  deposit_type: "Cash" | "Account transfer";
  /** The partner's email, which identifies them in the partners collection. */
  partner_email: string;
  valuation_status: "Locked" | "Pending";
  amount: string;
  benefit_pct: string;
  valuation_type: "" | "Type 1" | "Type 2";
  portfolio_value: string;
  fund_value_override: string;
}

type Row = Record<string, string | number | null>;

// Local calendar date as YYYY-MM-DD.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const EMPTY: FormState = {
  date_of_deposit: today(),
  action: "Deposit",
  deposit_type: "Cash",
  partner_email: "",
  valuation_status: "Locked",
  amount: "",
  benefit_pct: "0",
  valuation_type: "Type 2",
  portfolio_value: "",
  fund_value_override: "",
};

// Columns shown in the preview, in sheet order.
const PREVIEW_FIELDS = [
  "SSN Fund Value (Per Txn)",
  "SSN Units Bought (Per Txn)",
  "Month of Deposit",
];

function errorText(e: any): string {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => `${(d.loc ?? []).filter((l: any) => l !== "body").join(".") || "form"}: ${String(d.msg).replace(/^Value error, /, "")}`)
      .join("\n");
  }
  if (typeof detail === "string") return detail;
  return "Request failed. Please try again.";
}

function toPayload(f: FormState) {
  const num = (v: string) => (v.trim() === "" ? null : Number(v));
  return {
    date_of_deposit: f.date_of_deposit,
    action: f.action,
    deposit_type: f.deposit_type,
    partner_email: f.partner_email,
    valuation_status: f.valuation_status,
    amount: num(f.amount),
    benefit_pct: num(f.benefit_pct) ?? 0,
    valuation_type: f.valuation_type || null,
    portfolio_value: num(f.portfolio_value),
    fund_value_override: num(f.fund_value_override),
  };
}

function fmt(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "-";
  if (typeof v === "number") return v.toLocaleString("en-US", { maximumFractionDigits: 4 });
  return v;
}

interface AddTransactionFormProps {
  /** When set, the form edits this transaction instead of adding a new one. */
  editId?: string;
  initial?: FormState;
  onSaved?: (message: string) => void;
  onCancel?: () => void;
}

export default function AddTransactionForm({ editId, initial, onSaved, onCancel }: AddTransactionFormProps) {
  const { isMobile } = useResponsive();
  const [form, setForm] = useState<FormState>(initial ?? EMPTY);
  const [partners, setPartners] = useState<Partner[] | null>(null);
  const [preview, setPreview] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    api.get<Partner[]>("/partners")
      .then(({ data }) => setPartners(data))
      .catch(() => setPartners([]));
  }, []);

  const selected = partners?.find((p) => p.id === form.partner_email.toLowerCase());

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => {
      return { ...f, [key]: value };
    });
    // Any edit invalidates the previewed numbers.
    setPreview(null);
    setError(null);
    setSaved(null);
  };

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<Row>("/transactions/preview", toPayload(form), {
        params: editId ? { id: editId } : undefined,
      });
      setPreview(data);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = editId
        ? await api.put<Row>(`/transactions/${editId}`, toPayload(form))
        : await api.post<Row>("/transactions", toPayload(form));
      const message = `${editId ? "Updated" : "Saved"} ${data["Partner"]}'s ${fmt(data["Cash/Account Transfer Amount"])} deposit on ${data["Date of Deposit"]}.`;
      setSaved(message);
      setForm({ ...EMPTY, date_of_deposit: today() });
      setPreview(null);
      onSaved?.(message);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const locked = form.valuation_status === "Locked";
  const grid: React.CSSProperties = {
    ...S.grid,
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(220px, 1fr))",
  };

  return (
    <div style={S.card}>
      <h3 style={S.title}>{editId ? "Edit Transaction" : "Add Transaction"}</h3>
      <p style={S.hint}>
        Partner details come from the Partners tab and can't be changed here. Units bought are the amount
        divided by the unit price; enter the unit price after any benefit discount. Preview the row, then save it.
      </p>

      <div style={grid}>
        <Field label="Partner">
          <select style={S.input} value={selected?.id ?? ""} onChange={(e) => set("partner_email", e.target.value)}>
            <option value="">{partners === null ? "Loading…" : "Select a partner"}</option>
            {(partners ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Email">
          <input style={{ ...S.input, ...S.readOnly }} value={selected?.email ?? ""} readOnly tabIndex={-1} placeholder="From partner" />
        </Field>
        <Field label="Date of deposit">
          <input style={S.input} type="date" max={today()} value={form.date_of_deposit}
            onChange={(e) => set("date_of_deposit", e.target.value)} />
        </Field>
        <Field label="Amount ($)">
          <input style={S.input} type="number" min="0" step="0.01" value={form.amount}
            onChange={(e) => set("amount", e.target.value)} placeholder="25000" />
        </Field>
        <Field label="Action">
          <select style={S.input} value={form.action} onChange={(e) => set("action", e.target.value as FormState["action"])}>
            <option>Deposit</option>
            <option>Initial</option>
          </select>
        </Field>
        <Field label="Deposit type">
          <select style={S.input} value={form.deposit_type} onChange={(e) => set("deposit_type", e.target.value as FormState["deposit_type"])}>
            <option>Cash</option>
            <option>Account transfer</option>
          </select>
        </Field>
        <Field label="Valuation status">
          <select style={S.input} value={form.valuation_status} onChange={(e) => set("valuation_status", e.target.value as FormState["valuation_status"])}>
            <option>Locked</option>
            <option>Pending</option>
          </select>
        </Field>
        <Field label="Valuation type">
          <select style={S.input} value={form.valuation_type} onChange={(e) => set("valuation_type", e.target.value as FormState["valuation_type"])}>
            <option value="">None</option>
            <option>Type 1</option>
            <option>Type 2</option>
          </select>
        </Field>
        <Field label="% benefits">
          <input style={S.input} type="number" min="0" max="99" step="0.01" value={form.benefit_pct}
            onChange={(e) => set("benefit_pct", e.target.value)} />
        </Field>
        <Field label={`Unit price${locked ? "" : " (optional)"}`}>
          <input style={S.input} type="number" min="0" step="0.0001" value={form.fund_value_override}
            onChange={(e) => set("fund_value_override", e.target.value)} placeholder="15.37" />
        </Field>
      </div>

      {partners && !selected && form.partner_email && (
        <div style={S.warning}>
          {form.partner_email} isn't in the partner list. Add them in the Partners tab, then pick them here.
        </div>
      )}
      {partners?.length === 0 && (
        <div style={S.warning}>No partners yet. Add one in the Partners tab first.</div>
      )}
      {error && <pre style={S.error}>{error}</pre>}
      {saved && <div style={S.success}>{saved}</div>}

      {preview && (
        <div style={S.preview}>
          {PREVIEW_FIELDS.map((k) => (
            <div key={k} style={S.previewItem}>
              <span style={S.previewLabel}>{k}</span>
              <span style={S.previewValue}>{fmt(preview[k])}</span>
            </div>
          ))}
        </div>
      )}

      <div style={S.actions}>
        <button style={S.secondaryBtn} disabled={busy} onClick={runPreview}>
          {busy && !preview ? "Calculating…" : "Preview"}
        </button>
        <button style={{ ...S.primaryBtn, ...(!preview || busy ? S.disabled : {}) }}
          disabled={!preview || busy} onClick={save}>
          {busy && preview ? "Saving…" : editId ? "Save changes" : "Save transaction"}
        </button>
        {onCancel && (
          <button style={S.secondaryBtn} disabled={busy} onClick={onCancel}>Cancel</button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={S.field}>
      <span style={S.label}>{label}</span>
      {children}
    </label>
  );
}

const S: Record<string, React.CSSProperties> = {
  card: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12, padding: "24px 20px",
    boxSizing: "border-box",
  },
  title: { fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 6 },
  hint: { fontSize: 12, color: "var(--text-muted)", marginBottom: 20 },
  grid: { display: "grid", gap: 16 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: {
    fontSize: 11, fontWeight: 500, letterSpacing: "0.08em",
    textTransform: "uppercase", color: "var(--text-muted)",
  },
  input: {
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 8, padding: "10px 12px",
    color: "var(--text-primary)", fontSize: 14,
    colorScheme: "dark", width: "100%",
  },
  readOnly: { opacity: 0.6, cursor: "default" },
  warning: {
    marginTop: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(201,149,42,0.12)", border: "1px solid rgba(201,149,42,0.4)",
    color: "var(--gold-light)", fontSize: 13,
  },
  error: {
    marginTop: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(224,90,90,0.12)", border: "1px solid rgba(224,90,90,0.4)",
    color: "#f08a8a", fontSize: 13, fontFamily: "var(--font-body)", whiteSpace: "pre-wrap",
  },
  success: {
    marginTop: 16, padding: "10px 12px", borderRadius: 8,
    background: "rgba(29,158,117,0.12)", border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13,
  },
  preview: {
    marginTop: 20, display: "grid", gap: 12,
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    padding: 16, borderRadius: 8, background: "rgba(255,255,255,0.03)",
  },
  previewItem: { display: "flex", flexDirection: "column", gap: 4 },
  previewLabel: { fontSize: 11, color: "var(--text-muted)" },
  previewValue: { fontSize: 15, fontWeight: 600, color: "var(--text-primary)" },
  actions: { display: "flex", gap: 12, marginTop: 20, flexWrap: "wrap" },
  secondaryBtn: {
    padding: "10px 18px", borderRadius: 8, fontSize: 14, fontWeight: 500,
    border: "1px solid rgba(255,255,255,0.2)", color: "var(--text-primary)",
  },
  primaryBtn: {
    padding: "10px 18px", borderRadius: 8, fontSize: 14, fontWeight: 600,
    background: "var(--teal)", color: "#fff",
  },
  disabled: { opacity: 0.4, cursor: "not-allowed" },
};
